import React from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useSession } from '../app/providers/SessionProvider.jsx';
import { signIn, signOut } from '../services/account/accountService.js';
import { supabase } from '../services/supabase/client.js';
import {
  listMyTaxDocuments,
  loadMyContractorAccess,
  saveMyContractorOnboarding,
} from '../services/vimPay/contractorService.js';
import VimPayW9Builder from '../components/vimPay/VimPayW9Builder.jsx';

function StatusBadge({ children, tone = 'gold' }) {
  return <span className={`vim-pay-badge ${tone}`}>{children}</span>;
}

function human(value, fallback = 'Not set') {
  const text = String(value || '').replace(/_/g, ' ').trim();
  return text ? text.replace(/\b\w/g, (letter) => letter.toUpperCase()) : fallback;
}

function useVimPayAccess() {
  const { ready, user } = useSession();
  const [access, setAccess] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const reload = React.useCallback(async () => {
    if (!ready || !user?.id) return null;
    setLoading(true);
    setError('');
    try {
      const result = await loadMyContractorAccess(user.id);
      setAccess(result);
      return result;
    } catch (err) {
      setError(err?.message || 'Unable to verify contractor access.');
      return null;
    } finally {
      setLoading(false);
    }
  }, [ready, user?.id]);

  React.useEffect(() => { reload(); }, [reload]);
  return { ready, user, access, setAccess, loading, error, reload };
}

async function leaveVimPay() {
  await signOut();
  window.location.assign('/pay/login');
}

function AccessState({ ready, user, access, loading, error }) {
  if (ready && !user) return <Navigate to="/pay/login" replace />;
  if (error) {
    return (
      <section className="vim-pay-card vim-pay-center-card">
        <StatusBadge tone="red">ACCESS CHECK FAILED</StatusBadge>
        <h1>We could not verify your VIM Pay access.</h1>
        <p>{error}</p>
        <button className="vim-pay-secondary" type="button" onClick={leaveVimPay}>Sign out</button>
      </section>
    );
  }
  if (!ready || loading || !access) {
    return (
      <section className="vim-pay-card vim-pay-center-card">
        <StatusBadge>VIM PAY</StatusBadge>
        <h1>Verifying worker access…</h1>
      </section>
    );
  }
  if (!access.configured) {
    return (
      <section className="vim-pay-card vim-pay-center-card">
        <StatusBadge tone="red">SYSTEM SETUP</StatusBadge>
        <h1>VIM Pay is not fully configured.</h1>
        <p>The worker authorization database is not available yet.</p>
        <button className="vim-pay-secondary" type="button" onClick={leaveVimPay}>Sign out</button>
      </section>
    );
  }
  if (!access.authorized) {
    return (
      <section className="vim-pay-card vim-pay-center-card">
        <StatusBadge tone="red">NOT ENROLLED</StatusBadge>
        <h1>This account does not have VIM Pay access.</h1>
        <p>VIM Pay is available to invited talent, crew, and approved film-sales payees. Streaming membership access does not grant contractor access.</p>
        <button className="vim-pay-secondary" type="button" onClick={leaveVimPay}>Use a different account</button>
      </section>
    );
  }
  return null;
}

export function VimPayAuthCallbackPage() {
  const [params] = useSearchParams();
  const [error, setError] = React.useState('');
  const started = React.useRef(false);

  React.useEffect(() => {
    if (started.current) return;
    started.current = true;

    const tokenHash = String(params.get('token_hash') || '').trim();
    const type = String(params.get('type') || '').trim().toLowerCase();
    const allowedTypes = new Set(['invite', 'magiclink', 'signup', 'email']);

    if (!supabase) {
      setError('VIM Pay authentication is temporarily unavailable.');
      return;
    }
    if (!tokenHash || !allowedTypes.has(type)) {
      setError('This VIM Pay onboarding link is incomplete or invalid. Request a new onboarding email from VIM Media.');
      return;
    }

    supabase.auth.verifyOtp({ token_hash: tokenHash, type })
      .then(({ error: verifyError }) => {
        if (verifyError) throw verifyError;
        window.history.replaceState({}, '', '/pay/auth');
        window.location.replace('/pay/onboarding');
      })
      .catch((verifyError) => {
        setError(verifyError?.message || 'This VIM Pay onboarding link could not be verified. Request a new onboarding email from VIM Media.');
      });
  }, [params]);

  return (
    <section className="vim-pay-card vim-pay-center-card">
      <StatusBadge>VIM PAY</StatusBadge>
      <h1>{error ? 'Onboarding link needs attention' : 'Opening VIM Pay…'}</h1>
      <p>{error || 'Securely verifying your VIM Media worker invitation and opening your onboarding profile.'}</p>
      {error ? <a className="vim-pay-secondary" href="/pay/login">Go to VIM Pay sign in</a> : null}
    </section>
  );
}

export function VimPayLoginPage() {
  const { ready, user } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  if (ready && user) return <Navigate to="/pay" replace />;

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await signIn(email, password);
      navigate('/pay', { replace: true });
    } catch (err) {
      setError(err?.message || 'Unable to sign in to VIM Pay.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="vim-pay-auth-grid">
      <div className="vim-pay-hero-card">
        <StatusBadge>VIM MEDIA</StatusBadge>
        <h1>Payment and tax onboarding in one secure place.</h1>
        <p>VIM Pay is the secure portal for VIM Media contractors, film-sales payees, and payroll workers.</p>
        <div className="vim-pay-method-row"><span>ACH</span><span>Check</span><span>Commission: PayPal/Zelle</span></div>
      </div>

      <form className="vim-pay-card vim-pay-login-card" onSubmit={submit}>
        <div>
          <span className="vim-pay-kicker">INVITATION ONLY</span>
          <h2>Sign in to VIM Pay</h2>
          <p>Use the email connected to your VIM Media worker invitation.</p>
        </div>
        <label>Email<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
        <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
        {error ? <div className="vim-pay-error" role="alert">{error}</div> : null}
        <button className="vim-pay-primary" type="submit" disabled={busy || !ready}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <p className="vim-pay-help">VIM Pay accounts are created by invitation. There is no public contractor signup.</p>
      </form>
    </section>
  );
}

export function VimPayDashboardPage() {
  const navigate = useNavigate();
  const state = useVimPayAccess();
  const gate = <AccessState {...state} />;
  if (gate.type !== React.Fragment && (!state.access?.authorized || state.error || !state.ready || state.loading || !state.access)) return gate;

  const { access } = state;
  if (!access.phase2Configured) {
    return (
      <section className="vim-pay-card vim-pay-center-card">
        <StatusBadge>ONBOARDING SCHEMA</StatusBadge>
        <h1>Worker onboarding is being activated.</h1>
        <p>Your VIM Pay access is recognized, but the required onboarding and tax-document fields are not active in the database yet.</p>
        <button className="vim-pay-secondary" type="button" onClick={leaveVimPay}>Sign out</button>
      </section>
    );
  }

  const profile = access.profile || {};
  const unionLabel = profile.union_status === 'union'
    ? [profile.union_name, profile.union_local].filter(Boolean).join(' • ') || 'Union'
    : human(profile.union_status);

  return (
    <section className="vim-pay-dashboard">
      <div className="vim-pay-dashboard-heading">
        <div>
          <span className="vim-pay-kicker">WORKER PORTAL — PHASE 6.2</span>
          <h1>Welcome{profile.display_name ? `, ${profile.display_name}` : ''}</h1>
          <p>Complete your worker profile, payout preference, and the tax step assigned to your engagement type.</p>
        </div>
        <div className="vim-pay-actions">
          <button className="vim-pay-primary" type="button" onClick={() => navigate('/pay/onboarding')}>Continue onboarding</button>
          <button className="vim-pay-secondary" type="button" onClick={leaveVimPay}>Sign out</button>
        </div>
      </div>

      <div className="vim-pay-stat-grid">
        <article className="vim-pay-card"><span className="vim-pay-kicker">ONBOARDING</span><strong>{human(profile.onboarding_status).toUpperCase()}</strong><p>System-derived from your assigned worker type and completed steps.</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">ENGAGEMENT</span><strong>{human(profile.engagement_type).toUpperCase()}</strong><p>Your classification is assigned by VIM Media and cannot be changed from the worker portal.</p></article>
        {profile.engagement_type === 'commission_contractor' ? <article className="vim-pay-card"><span className="vim-pay-kicker">COMMISSION RATE</span><strong>{Number(profile.commission_rate_percent || 0).toFixed(4).replace(/0+$/,'').replace(/\.$/,'')}%</strong><p>Your approved commission percentage is controlled by VIM Media. Finance applies it to the eligible commissionable amount when processing payment.</p></article> : null}
        <article className="vim-pay-card"><span className="vim-pay-kicker">UNION STATUS</span><strong>{unionLabel.toUpperCase()}</strong><p>Union/non-union status is controlled by VIM Media for payroll and fringe processing.</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">TAX STATUS</span><strong>{human(profile.tax_status).toUpperCase()}</strong><p>{profile.engagement_type === 'payroll_employee' ? 'Payroll tax onboarding and net-pay calculations are available in the Tax section.' : 'Independent contractors complete or upload a W-9 for review.'}</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">PAYOUT PREFERENCE</span><strong>{human(profile.payout_method).toUpperCase()}</strong><p>{profile.engagement_type === 'commission_contractor' ? 'Commission contractors may choose PayPal, Zelle, or ACH.' : 'Independent contractors and payroll employees may choose ACH or Check.'} Actual payment execution begins in Phase 7.</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">FRINGE PROFILE</span><strong>{profile.fringe_profile_key || 'NOT ASSIGNED'}</strong><p>Assigned by VIM Media for payroll fringe calculations; rates are not editable from the worker portal.</p></article>
      </div>

      <div className="vim-pay-card vim-pay-roadmap-card">
        <StatusBadge>PHASE 6.2</StatusBadge>
        <h2>Payroll & tax ready</h2>
        <ul>
          <li>CMS-issued worker invitations and VIM-controlled worker classification</li>
          <li>Projects, assignments, approved roles and rates</li>
          <li>Payment requests with Production and Finance approval controls</li>
          <li>Payroll fringe calculations for configured payroll profiles</li>
          <li>Employee withholding and net-pay calculations in the Tax section</li>
          <li>Secure electronic W-9 builder with private signed-PDF storage for independent and commission-based contractors</li>
          <li>Payment preference is classification-specific: commission contractors may use PayPal/Zelle/ACH; independent contractors and employees use ACH/Check. Actual PayPal/Zelle/ACH execution and payroll-provider transmission remain disabled until Phase 7.</li>
        </ul>
      </div>
    </section>
  );
}

export function VimPayOnboardingPage() {
  const navigate = useNavigate();
  const state = useVimPayAccess();
  const { user, access, setAccess, reload } = state;
  const [form, setForm] = React.useState({ country_code: 'US', payout_method: '' });
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState('');
  const [formError, setFormError] = React.useState('');
  const [taxDocuments, setTaxDocuments] = React.useState([]);
  const profile = access?.profile || {};

  React.useEffect(() => {
    if (!access?.authorized || !access.phase2Configured || !profile.id) return;
    setForm({
      display_name: profile.display_name || '',
      legal_name: profile.legal_name || '',
      phone: profile.phone || '',
      address_line1: profile.address_line1 || '',
      address_line2: profile.address_line2 || '',
      city: profile.city || '',
      state_region: profile.state_region || '',
      postal_code: profile.postal_code || '',
      country_code: profile.country_code || 'US',
      payout_method: profile.payout_method || '',
      payout_contact: profile.payout_contact || '',
    });
  }, [access?.authorized, access?.phase2Configured, profile.id, profile.updated_at]);

  React.useEffect(() => {
    if (!access?.authorized || !access.phase2Configured || !['independent_contractor', 'commission_contractor'].includes(profile.engagement_type) || !profile.id) {
      setTaxDocuments([]);
      return;
    }
    listMyTaxDocuments(profile.id).then(setTaxDocuments).catch(() => setTaxDocuments([]));
  }, [access?.authorized, access?.phase2Configured, profile.id, profile.engagement_type, profile.tax_status]);

  const gate = <AccessState {...state} />;
  if (!access?.authorized || state.error || !state.ready || state.loading || !access) return gate;
  if (!access.phase2Configured) return <Navigate to="/pay" replace />;

  function field(name, value) {
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setFormError('');
    try {
      const updated = await saveMyContractorOnboarding(user.id, form);
      setAccess((current) => ({ ...current, profile: updated, phase2Configured: true }));
      setMessage('Your VIM Pay profile has been saved.');
      await reload();
    } catch (err) {
      setFormError(err?.message || 'Unable to save your VIM Pay profile.');
    } finally {
      setSaving(false);
    }
  }

  async function refreshW9() {
    setTaxDocuments(await listMyTaxDocuments(profile.id));
    await reload();
  }

  const isContractor = ['independent_contractor', 'commission_contractor'].includes(profile.engagement_type);
  const isCommissionContractor = profile.engagement_type === 'commission_contractor';
  const isPayrollEmployee = profile.engagement_type === 'payroll_employee';

  return (
    <section className="vim-pay-dashboard">
      <div className="vim-pay-dashboard-heading">
        <div><span className="vim-pay-kicker">PHASE 6.2 ONBOARDING</span><h1>Worker setup</h1><p>Complete only the information requested below. Worker classification and union status are managed by VIM Media.</p></div>
        <button className="vim-pay-secondary" type="button" onClick={() => navigate('/pay')}>Back to dashboard</button>
      </div>

      <div className="vim-pay-classification-grid">
        <article className="vim-pay-card"><span className="vim-pay-kicker">ASSIGNED ENGAGEMENT</span><strong>{human(profile.engagement_type).toUpperCase()}</strong><p>This classification is locked in the worker portal.</p></article>
        <article className="vim-pay-card"><span className="vim-pay-kicker">ASSIGNED UNION STATUS</span><strong>{human(profile.union_status).toUpperCase()}</strong><p>{profile.union_status === 'union' ? [profile.union_name, profile.union_local].filter(Boolean).join(' • ') || 'Union details pending' : 'No union fringe profile will be assumed.'}</p></article>
      </div>

      <form className="vim-pay-card vim-pay-onboarding-form" onSubmit={save}>
        <div className="vim-pay-form-section"><span className="vim-pay-kicker">PROFILE</span><h2>Legal and contact information</h2><p>Used for worker onboarding and future payment records.</p></div>
        <div className="vim-pay-form-grid">
          <label>Legal name<input value={form.legal_name || ''} onChange={(e) => field('legal_name', e.target.value)} required /></label>
          <label>Preferred/display name<input value={form.display_name || ''} onChange={(e) => field('display_name', e.target.value)} /></label>
          <label>Phone<input type="tel" value={form.phone || ''} onChange={(e) => field('phone', e.target.value)} required /></label>
          <label className="span-2">Mailing address<input value={form.address_line1 || ''} onChange={(e) => field('address_line1', e.target.value)} required /></label>
          <label className="span-2">Address line 2<input value={form.address_line2 || ''} onChange={(e) => field('address_line2', e.target.value)} /></label>
          <label>City<input value={form.city || ''} onChange={(e) => field('city', e.target.value)} required /></label>
          <label>State/region<input value={form.state_region || ''} onChange={(e) => field('state_region', e.target.value)} required /></label>
          <label>Postal code<input value={form.postal_code || ''} onChange={(e) => field('postal_code', e.target.value)} required /></label>
          <label>Country code<input maxLength="2" value={form.country_code || 'US'} onChange={(e) => field('country_code', e.target.value.toUpperCase())} required /></label>
        </div>

        <div className="vim-pay-form-section">
          <span className="vim-pay-kicker">PAYOUT PREFERENCE</span>
          <h2>{isCommissionContractor ? 'Choose PayPal, Zelle, or ACH' : 'Choose ACH or Check'}</h2>
          <p>{isCommissionContractor ? 'Commission contractors may use PayPal, Zelle, or ACH.' : 'Independent contractors and payroll employees are paid by ACH or Check.'} This selection does not send money or authorize a payout.</p>
        </div>
        <div className="vim-pay-form-grid">
          <label>Payout preference
            <select value={form.payout_method || ''} onChange={(e) => field('payout_method', e.target.value)}>
              <option value="">Select later</option>
              {isCommissionContractor ? <option value="paypal">PayPal</option> : null}
              {isCommissionContractor ? <option value="zelle">Zelle</option> : null}
              <option value="ach">ACH</option>
              {!isCommissionContractor ? <option value="check">Check</option> : null}
            </select>
          </label>
          {isCommissionContractor && ['paypal', 'zelle'].includes(form.payout_method) ? (
            <label>{form.payout_method === 'paypal' ? 'PayPal email' : 'Zelle email or phone'}<input value={form.payout_contact || ''} onChange={(e) => field('payout_contact', e.target.value)} /></label>
          ) : null}
          {form.payout_method === 'ach' ? (
            <div className="vim-pay-safe-note span-2"><strong>ACH safety</strong><p>Do not enter bank details here. Secure ACH enrollment is handled through the approved payroll/payment workflow.</p></div>
          ) : null}
          {form.payout_method === 'check' ? (
            <div className="vim-pay-safe-note span-2"><strong>Check delivery</strong><p>Your mailing address above will be used for the check payment record. Confirm it is current before saving.</p></div>
          ) : null}
        </div>

        {message ? <div className="vim-pay-success" role="status">{message}</div> : null}
        {formError ? <div className="vim-pay-error" role="alert">{formError}</div> : null}
        <div className="vim-pay-actions"><button className="vim-pay-primary" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save onboarding profile'}</button></div>
      </form>

      {isContractor ? (
        <>
        {isCommissionContractor ? <div className="vim-pay-safe-note"><strong>Commission-only engagement</strong><p>Your commission percentage is assigned by VIM Media. You do not enter commissionable revenue or calculate your own payout here; VIM Finance calculates payment from the approved commission rate.</p></div> : null}
        <div className="vim-pay-safe-note"><strong>W-9 required before film-sales payment</strong><p>U.S. payees must complete a signed W-9 and have it verified by VIM Finance before receiving film-sales payments. This includes payees who have already received revenue. We use your tax information for applicable year-end reporting, including Form 1099 where required.</p><p>If you are not a U.S. person for tax purposes, do not sign a W-9. Contact payroll@watchvim.us for the appropriate W-8 review. Do not email tax forms or tax identification numbers.</p></div>
        <VimPayW9Builder userId={user.id} profile={profile} taxDocuments={taxDocuments} onSubmitted={refreshW9} />
        </>
      ) : null}

      {isPayrollEmployee ? (
        <section className="vim-pay-card vim-pay-onboarding-form">
          <div className="vim-pay-form-section"><span className="vim-pay-kicker">PAYROLL EMPLOYEE TAX STEP</span><h2>Complete payroll tax setup in the Tax section</h2><p>A W-9 is not requested for this engagement. W-4/A-4 onboarding, payroll withholding, net-pay calculation, union classification, and configured fringe calculations are available in VIM Pay Phase 6.2.</p></div>
          <div className="vim-pay-safe-note"><strong>Classification protection</strong><p>You cannot switch yourself between payroll employee, independent contractor, or commission-based contractor. Contact VIM Media if the assignment shown above is incorrect.</p></div>
          <div className="vim-pay-actions"><button className="vim-pay-primary" type="button" onClick={() => navigate('/pay/tax')}>Open Tax setup</button></div>
        </section>
      ) : null}
    </section>
  );
}

